import {
  CareerCourseAccess,
  CareerCourseReason,
  ICareerCourseRecommendation,
  ICareerSkillNode,
} from 'core/hooks/CareerSkillGraph/transform/transformCareerSkillGraph';

/**
 * Курсы в паспорте навыка (ТЗ 0071, п. 6.4).
 *
 * До этой правки паспорт показывал ОДНОВРЕМЕННО основной курс, полный список
 * «Gdzie tego uczą» и ещё «Inne możliwości» — один и тот же курс мог стоять в
 * паспорте трижды, и вопрос «что мне делать» тонул в каталоге. ТЗ требует одну
 * рекомендацию и свёрнутый список альтернатив без повторов.
 *
 * Чистая функция, потому что «курс повторился» глазами замечают через месяц,
 * а тестом — сразу.
 */

export interface IPassportCourse {
  courseId: string;
  courseName: string;
  courseUrl: string;
  access: CareerCourseAccess;
  /** Имена пробелов, которые курс закроет. */
  closesSkillNames: string[];
  /** '' — причину назвать нечем (курс взят из «где учат», а не из подбора). */
  reasonCode: CareerCourseReason;
  criticalGaps: number;
  importantGaps: number;
  completed: boolean;
}

export interface IPassportCourses {
  primary: IPassportCourse | null;
  alternatives: IPassportCourse[];
}

/**
 * Чем ближе курс к «уже моё», тем выше: продолжить начатое — самый короткий
 * путь к пробелу, и предлагать рядом покупку второго курса значит говорить
 * «школа не знает, что я у неё учусь».
 */
const ACCESS_RANK: Record<CareerCourseAccess, number> = {
  in_progress: 0,
  purchased_not_started: 1,
  available: 2,
};

/**
 * 🔴 Подпись кнопки — словарём ЛИТЕРАЛОВ: сканер паритета локалей видит только
 * их, и шаблон ключа по `access` прошёл бы мимо него.
 */
export const COURSE_CTA_KEY: Record<CareerCourseAccess, string> = {
  in_progress: 'careerCenter.map.course.continue',
  purchased_not_started: 'careerCenter.map.course.start',
  available: 'careerCenter.map.course.program',
};

const fromRecommendation = (row: ICareerCourseRecommendation): IPassportCourse => ({
  courseId: row.courseId,
  courseName: row.courseName,
  courseUrl: row.courseUrl,
  // Старое поле `status` знает только «записан»; новое `access` — точнее. Если
  // сервер старше фронта и `access` пуст, «записан» всё равно не станет
  // предложением купить.
  access: row.access === 'available' && row.status === 'enrolled' ? 'in_progress' : row.access,
  closesSkillNames: row.closesSkillNames,
  reasonCode: row.reasonCode,
  criticalGaps: row.criticalGaps,
  importantGaps: row.importantGaps,
  completed: false,
});

export const passportCourses = (
  node: ICareerSkillNode,
  recommended: ICareerCourseRecommendation[],
): IPassportCourses => {
  /**
   * 🔴 Подбор сервера — на ВСЮ карту, а не на этот навык. Курс, который этот
   * пробел не закрывает, в паспорте навыка — реклама, а не рекомендация.
   * `closesNodes` несёт СЛАГИ узлов (см. `course_recommendation.for_graph`).
   */
  const matching = recommended
    .filter((row) => row.closesNodes.includes(node.slug))
    .map(fromRecommendation)
    .sort((a, b) => ACCESS_RANK[a.access] - ACCESS_RANK[b.access]);

  // «Где учат» — сырьё для альтернатив и запасной основной вариант, если
  // подбор этот навык не покрыл. Пройденный курс основным не предлагается:
  // навык из него уже должен был засчитаться.
  const providers: IPassportCourse[] = node.providers.map((row) => ({
    courseId: row.courseId,
    courseName: row.courseName,
    courseUrl: row.courseUrl,
    access: 'available',
    closesSkillNames: [node.name],
    reasonCode: '',
    criticalGaps: 0,
    importantGaps: 0,
    completed: row.completed,
  }));

  // Навык уже на уровне — вести его на курс незачем; альтернативы остаются
  // справкой в «подробнее».
  const needsCourse = node.requirementStatus !== 'meets';
  const primary = needsCourse
    ? matching[0] || providers.find((row) => !row.completed) || null
    : null;

  const seen = new Set<string>(primary ? [primary.courseId] : []);
  const alternatives = [...matching, ...providers].filter((row) => {
    if (seen.has(row.courseId)) return false;
    seen.add(row.courseId);
    return true;
  });

  return { primary, alternatives };
};
